/**
 * placeholder 行の `deliveryId`。実体 (shift_truck) が無い行に振る負の合成 ID。
 *
 * `rowIdFromTruck` はこの値を `rowId` に焼き込み、`rowId` は dnd-kit の droppable id かつ
 * D&D の楽観的上書き `overrideRowCards` のキーである。したがって **同じ行に対して常に同じ値を
 * 返さないと、その行に置いたカードが消える**（log_web_issues#304）。
 *
 * Why not 生成順の連番: 以前は `placeholderIdCounter--` / `placeholderSeed--` で列挙順に
 * 振っていた。どちらの生成ループもスキップ条件が外から変わる（固定ルート由来は
 * `deliveries` 由来の `deliveryKeySet`、回転追加は `extraRotations` の挿入順）ため、
 * 無関係な増減で後続の行の ID がずれていた。行のキーから決定的に導けばずれない。
 *
 * Why not 種別を混ぜた 1 つの関数: 固定ルート由来のキーは `(truckId, rotationNumber)`、
 * 回転追加のキーは `(deliveryId, rotationNumber)` で名前空間が違う。`truckId` と
 * `deliveryId` はそれぞれ別テーブルの ID なので同値になり得る。同じ計算式に入れると
 * 同じ `rowId` を作ってしまい、dnd-kit の droppable id が重複する。種別ごとに帯を分ける。
 */

/**
 * 種別ごとに割り当てる値域の幅。`キー * ROTATION_STRIDE + 回転番号` がこの幅に収まる前提。
 * shift_truck / truck の ID が 10 億に達すると隣の帯へ食い込むが、そこまで増える前に
 * ID 体系ごと見直しが要る規模なので、安全整数 (9.0e15) の余裕を残すこの幅にしている。
 */
const BAND = 1_000_000_000_000;

/**
 * 1 つのキーに割り当てる回転番号の通り数。ID は `キー * ROTATION_STRIDE + 回転番号` で作るので、
 * 回転番号がこの値以上になると隣のキーの ID と衝突する。1 勤務 1000 回転は運用上あり得ない。
 */
const ROTATION_STRIDE = 1000;

/**
 * 固定ルートにしか存在しない行（`vehicleRowVM` の Step 4b）。
 * キーは `(truckId, rotationNumber)` で、この行はまだ勤務を持たない。
 */
export function fixedRoutePlaceholderDeliveryId(truckId: number, rotationNumber: number): number {
  return -(BAND + truckId * ROTATION_STRIDE + rotationNumber);
}

/**
 * ユーザーがローカルで追加した回転（`rotationEditing` の `mergeExtraRotations`）。
 * キーは追加先の `(deliveryId, rotationNumber)`。
 */
export function extraRotationPlaceholderDeliveryId(
  deliveryId: number,
  rotationNumber: number,
): number {
  return -(2 * BAND + deliveryId * ROTATION_STRIDE + rotationNumber);
}
